import { ArrowLeft, ArrowUpRight, FileWarning } from "lucide-react";

import { MarkdownContent } from "@/components/chat/MarkdownContent";
import { NotebookDocList } from "@/components/notebook/NotebookDocList";
import { NotebookEmptyState } from "@/components/notebook/NotebookEmptyState";
import type { NoteListItem } from "@/hooks/useNotebook";

export interface NotebookQuickViewUIProps {
  notes: NoteListItem[];
  query: string;
  isLoading: boolean;
  lastRefreshedAt: number | null;
  onQueryChange: (query: string) => void;
  onRefresh: () => void;
  /**
   * Promote to the full-page workspace at /notebook. A `path` promotes to that
   * exact note (the `?path=` deep link); omitting it opens the workspace as a
   * whole — so promoting from the preview lands ON the open note instead of
   * dumping the reader at "Select a note to read".
   */
  onOpenPage: (path?: string) => void;
  /** Path of the note being previewed in place, or null for the list. */
  openPath?: string | null;
  /** Body of the open note; null while it is still being read. */
  openContent?: string | null;
  isReading?: boolean;
  /** Read failure that is NOT not-found (transport / oversize) — display copy. */
  readError?: string | null;
  notFound?: boolean;
  onOpenNote?: (path: string) => void;
  onCloseNote?: () => void;
}

function titleOf(path: string): string {
  const last = path.split("/").pop() ?? path;
  return last.replace(/\.md$/i, "") || path;
}

/**
 * Presentational half of the Notebook prop window — a searchable peek at the
 * notebook, plus a read-only preview of one note. Pure: no data hooks (those
 * live in the `.container`), so it renders from props alone.
 *
 * The preview is deliberately READ-ONLY. Editing, sharing, deleting and moving
 * stay on the full page: re-hosting them in a floating window over the 3D world
 * would fork the notebook's write path across two surfaces. Reading is the one
 * thing worth doing without leaving the world you are standing in.
 */
export function NotebookQuickViewUI({
  notes,
  query,
  isLoading,
  lastRefreshedAt,
  onQueryChange,
  onRefresh,
  onOpenPage,
  openPath = null,
  openContent = null,
  isReading = false,
  readError = null,
  notFound = false,
  onOpenNote,
  onCloseNote,
}: NotebookQuickViewUIProps) {
  // ─── Preview of one note ────────────────────────────────────────────
  if (openPath !== null) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <button
          type="button"
          onClick={onCloseNote}
          className="mb-1.5 inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-[12px] font-bold text-black/60 transition-colors hover:bg-black/5 hover:text-black dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.4} />
          All notes
        </button>

        <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-black/8 px-4 py-3 dark:border-white/10">
          <h2 className="text-base font-bold text-black dark:text-white">
            {titleOf(openPath)}
          </h2>

          {isReading && (
            <p className="mt-3 text-xs text-black/45 dark:text-white/45">Loading note…</p>
          )}

          {/* A note that is GONE and a note that merely failed to load are
              different facts — never let a transport blip read as deletion. */}
          {!isReading && notFound && (
            <div className="mt-4 flex flex-col items-center gap-2 text-center">
              <FileWarning className="h-7 w-7 text-black/30 dark:text-white/30" strokeWidth={1.8} />
              <p className="text-sm font-bold text-black/70 dark:text-white/70">
                This note no longer exists.
              </p>
              <p className="max-w-xs text-xs text-black/45 dark:text-white/45">
                It may have been deleted or renamed. Pick another from the list.
              </p>
            </div>
          )}

          {!isReading && !notFound && readError !== null && (
            <div className="mt-4 flex flex-col items-center gap-2 text-center">
              <FileWarning className="h-7 w-7 text-black/30 dark:text-white/30" strokeWidth={1.8} />
              <div className="flex max-w-xs flex-col gap-2">
                <p className="text-sm font-bold text-black/70 dark:text-white/70">{readError}</p>
                <p className="text-xs text-black/45 dark:text-white/45">
                  The note is still there — it just couldn&apos;t be loaded right now.
                </p>
              </div>
            </div>
          )}

          {!isReading && !notFound && readError === null && (
            <div className="prose prose-sm mt-3 max-w-none dark:prose-invert">
              {(openContent ?? "").trim() ? (
                <MarkdownContent content={openContent ?? ""} />
              ) : (
                <p className="text-black/40 dark:text-white/40">This note is empty.</p>
              )}
            </div>
          )}
        </div>

        <button
          onClick={() => onOpenPage(openPath)}
          className="mt-2 flex shrink-0 items-center justify-center gap-1.5 rounded-xl border border-black/10 py-2.5 text-[13px] font-bold text-black/70 transition-colors hover:bg-black/5 dark:border-white/15 dark:text-white/70 dark:hover:bg-white/5"
        >
          Open in notebook
          <ArrowUpRight className="h-4 w-4" strokeWidth={2.4} />
        </button>
      </div>
    );
  }

  // ─── The list ───────────────────────────────────────────────────────
  if (!isLoading && notes.length === 0 && query.trim() === "") {
    return (
      <NotebookEmptyState
        onWriteNote={() => onOpenPage()}
        onAskAgent={() => onOpenPage()}
        onImport={() => onOpenPage()}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1">
        <NotebookDocList
          notes={notes}
          selectedPath={null}
          query={query}
          onQueryChange={onQueryChange}
          // Preview in place when the container wired it up; fall back to the
          // old promote-to-page behaviour so the `.ui` stays usable alone.
          onSelect={(path) => (onOpenNote ? onOpenNote(path) : onOpenPage(path))}
          onNewNote={() => onOpenPage()}
          onRefresh={onRefresh}
          isLoading={isLoading}
          lastRefreshedAt={lastRefreshedAt}
          onImport={() => onOpenPage()}
          className="flex !w-full border-r-0"
        />
      </div>
      <button
        onClick={() => onOpenPage()}
        className="mt-2 flex shrink-0 items-center justify-center gap-1.5 rounded-xl border border-black/10 py-2.5 text-[13px] font-bold text-black/70 transition-colors hover:bg-black/5 dark:border-white/15 dark:text-white/70 dark:hover:bg-white/5"
      >
        Open full notebook
        <ArrowUpRight className="h-4 w-4" strokeWidth={2.4} />
      </button>
    </div>
  );
}
